import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { applyDiscountsToCart } from "../utils/discountEngine";
import { fetchActiveDiscountRules } from "../services/discountService";
import { useAppConfig } from "../context/AppConfigContext";
import calculateTax from "../utils/calculateTax";
import { useAuth } from "./AuthContext";
import api from "../services/api";

export const CartContext = createContext();
export const useCart = () => useContext(CartContext);

// ---------- utils ----------
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const stableStringify = (obj) => {
  if (obj == null) return "";
  if (typeof obj !== "object") return String(obj);
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(",")}]`;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${k}:${stableStringify(obj[k])}`)
    .join(",")}}`;
};
const hash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
};
const buildLineId = ({ productId, type, options }) =>
  `prod:${productId}|type:${type || "na"}|opts:${hash(
    stableStringify(options || {})
  )}`;

const deepClone = (obj) => {
  if (typeof structuredClone === "function") return structuredClone(obj);
  return JSON.parse(JSON.stringify(obj));
};

// Ensure the incoming payload is cart-safe and normalized
const normalizeIncomingItem = (raw) => {
  const productId = raw.productId || raw._id || raw.id;
  const type = raw.type || raw.subcategory || raw.category || "misc";
  const listPrice = Number(
    raw.listPrice ??
      raw.originalPrice ??
      raw.regularPrice ??
      raw.msrp ??
      raw.price ??
      raw.baseUnitPrice ??
      raw.unitPrice ??
      0
  );
  const quantity = Math.max(1, Number(raw.quantity || 1));

  // 👇 keep BOTH keys; accept either on input
  const customizationArr = raw.customizations ?? raw.customization ?? [];

  const optionsForId = {
    flavor: raw.flavor || null,
    size: raw.size || null,
    color: raw.color || null,
    customizations: customizationArr,
  };

  const lineId =
    raw.lineId ||
    buildLineId({
      productId,
      type,
      options: optionsForId,
    });

  return {
    lineId,
    productId,
    type,
    category: raw.category || null,
    subcategory: raw.subcategory || null,
    name: raw.name || "",
    displayName: raw.displayName || null,
    image: raw.image || null,

    // Pricing baseline — the only price we store from UI
    listPrice,
    quantity,
    itemsPerSet: Number(raw.itemsPerSet || 1),

    // options
    flavor: raw.flavor || null,
    size: raw.size || null,
    color: raw.color || null,

    // keep both keys for compatibility
    customization: customizationArr,
    customizations: customizationArr,
  };
};

const CartProvider = ({ children }) => {
  const { user: authUser, isAuthenticated } = useAuth();

  const [cartItems, setCartItems] = useState([]);
  const [activeDiscounts, setActiveDiscounts] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [showCartPreview, setShowCartPreview] = useState(false);

  // Discount-engine "user" context (extend later)
  const [user] = useState({ isGuest: true, tags: [] });

  const { appConfig } = useAppConfig();
  const TAX_RATE = useMemo(
    () => appConfig?.taxes?.taxRate ?? 0.15,
    [appConfig?.taxes?.taxRate]
  );

  // -------- LocalStorage Hydration / Persistence --------
  useEffect(() => {
    const stored = localStorage.getItem("cartItems");
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored) || [];
      const repaired = parsed.map((i) => normalizeIncomingItem(i));
      setCartItems(repaired);
    } catch {
      setCartItems([]);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  // -------- Active Discounts --------
  useEffect(() => {
    const loadDiscounts = async () => {
      try {
        const res = await fetchActiveDiscountRules();
        setActiveDiscounts(res.data || []);
      } catch (error) {
        console.error("Discount fetch error:", error);
        setActiveDiscounts([]);
      }
    };
    loadDiscounts();
  }, []);

  // -------- Cart Operations (lineId-based) --------
  const addToCart = async (incoming) => {
    const item = normalizeIncomingItem(incoming);

    // optimistic local add/merge
    setCartItems((prev) => {
      const idx = prev.findIndex((x) => x.lineId === item.lineId);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx].quantity += item.quantity;
        return updated;
      }
      return [...prev, item];
    });
    setShowCartPreview(true);

    if (authUser && isAuthenticated) {
      try {
        await api.post("/cart/add", item);
        return { ok: true, synced: true, item };
      } catch (err) {
        console.error("Cart sync (add) failed:", err);
        return { ok: true, synced: false, error: err, item };
      }
    }

    return { ok: true, synced: false, item };
  };

  const updateCartItemQuantity = (lineId, newQuantity) => {
    const qty = Math.max(0, Number(newQuantity || 0));

    setCartItems((prev) => {
      if (qty === 0) {
        return prev.filter((x) => x.lineId !== lineId);
      }
      return prev.map((x) =>
        x.lineId === lineId ? { ...x, quantity: qty } : x
      );
    });

    if (authUser && isAuthenticated) {
      api
        .post("/cart/update", { lineId, quantity: qty })
        .catch((err) => console.error("Cart sync (update) failed:", err));
    }
  };

  const removeFromCart = async (lineIdOrItem) => {
    const lineId =
      typeof lineIdOrItem === "string" ? lineIdOrItem : lineIdOrItem?.lineId;

    if (!lineId) {
      console.error("removeFromCart: missing lineId");
      return { ok: false, synced: false, error: new Error("Missing lineId") };
    }

    setCartItems((prev) => prev.filter((x) => x.lineId !== lineId));

    if (authUser && isAuthenticated) {
      try {
        await api.delete("/cart/remove", { data: { lineId } });
        return { ok: true, synced: true, lineId };
      } catch (err) {
        console.error("Cart sync (remove) failed:", err);
        return { ok: true, synced: false, error: err, lineId };
      }
    }

    return { ok: true, synced: false, lineId };
  };

  const clearCart = async () => {
    setCartItems([]);
    if (authUser && isAuthenticated) {
      try {
        await api.delete("/cart/clear");
      } catch (err) {
        console.error("Failed to clear cart on backend:", err);
      }
    }
  };

  // -------- Discount Engine (cart-only) --------
  // Build a clean snapshot for the engine that baselines from listPrice.
  const engineInput = useMemo(
    () =>
      cartItems.map((it) => ({
        // identity & descriptive fields we want back:
        lineId: it.lineId,
        image: it.image,
        displayName: it.displayName,
        category: it.category,
        subcategory: it.subcategory,
        type: it.type,
        itemsPerSet: it.itemsPerSet,
        flavor: it.flavor,
        size: it.size,
        color: it.color,

        // 👇 send customizations through under BOTH keys
        customization: it.customization ?? it.customizations ?? [],
        customizations: it.customization ?? it.customizations ?? [],

        // keys used by engine pricing:
        _id: it.productId,
        productId: it.productId,
        quantity: it.quantity,
        baseUnitPrice: Number(it.listPrice),
        unitPrice: Number(it.listPrice),
      })),
    [cartItems]
  );

  const engineResult = useMemo(() => {
    const input = deepClone(engineInput);
    return applyDiscountsToCart(input, user, activeDiscounts, appliedCoupon);
  }, [engineInput, user, activeDiscounts, appliedCoupon]);

  // Re-attach descriptive fields (including customizations) by lineId
  const engineUpdatedItems = engineResult.updatedItems || [];
  const discountedCartItems = useMemo(() => {
    const byId = new Map(cartItems.map((ci) => [ci.lineId, ci]));
    return engineUpdatedItems.map((li) => {
      const src = byId.get(li.lineId) || {};
      const custom =
        src.customization ??
        src.customizations ??
        li.customization ??
        li.customizations ??
        [];
      return {
        // keep descriptive/meta from source line
        ...src,
        // let engine override numeric/pricing fields
        ...li,
        // ensure both keys carry the same array
        customization: custom,
        customizations: custom,
      };
    });
  }, [engineUpdatedItems, cartItems]);

  const {
    totalDiscount,
    originalSubtotal,
    discountedSubtotal,
    appliedDiscounts: cartDiscounts,
    internalDiscountMetadata: fullDiscountRules,
  } = engineResult;

  // Money/calcs
  const cartSubtotal = useMemo(
    () => round2(Number(originalSubtotal || 0)),
    [originalSubtotal]
  );

  const computedDiscountedSubtotal = useMemo(() => {
    if (typeof discountedSubtotal === "number")
      return round2(discountedSubtotal);
    return round2(
      (discountedCartItems || []).reduce(
        (sum, li) => sum + Number(li.totalPrice || 0),
        0
      )
    );
  }, [discountedSubtotal, discountedCartItems]);

  const taxAmount = useMemo(
    () => round2(calculateTax(discountedCartItems || [], TAX_RATE)),
    [discountedCartItems, TAX_RATE]
  );

  const cartTotal = useMemo(
    () => round2(computedDiscountedSubtotal + taxAmount),
    [computedDiscountedSubtotal, taxAmount]
  );

  const summary = useMemo(
    () => ({
      subtotal: cartSubtotal, // before discounts
      youSaved: round2(Number(totalDiscount || 0)),
      taxRate: TAX_RATE,
      taxAmount,
      total: cartTotal,
      discountedSubtotal: computedDiscountedSubtotal,
    }),
    [
      cartSubtotal,
      totalDiscount,
      TAX_RATE,
      taxAmount,
      cartTotal,
      computedDiscountedSubtotal,
    ]
  );

  return (
    <CartContext.Provider
      value={{
        // state
        cartItems,
        showCartPreview,
        activeDiscounts,
        appliedCoupon,

        // actions
        setCartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateCartItemQuantity,
        setShowCartPreview,
        setAppliedCoupon,

        // results from engine
        discountedCartItems,
        cartDiscounts,
        fullDiscountRules,

        // money
        TAX_RATE,
        cartSubtotal,
        totalDiscount,
        cartTotal,
        taxAmount,
        summary,
      }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
